<template>
  <div class="create-square bg-neutral-50 min-h-screen">
    <main class="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <!-- Hero -->
      <section class="text-center mb-10">
        <div
          class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-600 text-xs font-medium mb-4"
        >
          <i class="fa fa-lightbulb-o"></i>
          教师的 AI 创想与作品
        </div>
        <h1 class="text-3xl md:text-5xl font-bold text-neutral-900 mb-4">
          AI创想广场
        </h1>
        <p class="text-base md:text-lg text-neutral-500 max-w-2xl mx-auto">
          把你做出来的工具、案例、网站与提示词分享出来，也把教学中解决不了的问题提出来，让更多老师一起共创。
        </p>
      </section>

      <!-- 发布入口 -->
      <section class="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-10">
        <button
          v-for="p in publishEntries"
          :key="p.to"
          @click="goPublish(p)"
          class="group bg-white rounded-xl border border-neutral-200 p-4 md:p-5 text-left hover:border-primary hover:shadow-md transition-all"
        >
          <div class="flex items-center gap-2 mb-2">
            <span
              class="w-9 h-9 rounded-lg flex items-center justify-center text-white flex-shrink-0"
              :class="p.bg"
            >
              <i :class="['fa', p.icon]" class="text-sm"></i>
            </span>
            <span class="font-bold text-sm md:text-base text-neutral-900">{{
              p.label
            }}</span>
          </div>
          <p class="text-xs text-neutral-400 leading-relaxed">{{ p.desc }}</p>
        </button>
      </section>

      <!-- 分类 Tab -->
      <section class="mb-6">
        <div class="flex gap-2 overflow-x-auto pb-1">
          <button
            v-for="t in tabs"
            :key="t.id"
            @click="switchTab(t.id)"
            class="px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors"
            :class="
              tab === t.id
                ? 'bg-primary text-white font-medium'
                : 'bg-white text-neutral-600 border border-neutral-200 hover:border-primary hover:text-primary'
            "
          >
            {{ t.label }}
          </button>
        </div>
      </section>

      <!-- 加载 -->
      <div v-if="loading" class="text-center py-16 text-neutral-400">
        <i class="fa fa-circle-o-notch fa-spin text-2xl"></i>
      </div>

      <!-- 全部：按板块分组 -->
      <template v-else-if="tab === 'all'">
        <section
          v-for="group in groups"
          :key="group.type"
          v-show="group.items.length > 0"
          class="mb-10"
        >
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-bold text-neutral-900 flex items-center gap-2">
              <i :class="['fa', group.icon]" :style="{ color: group.color }"></i>
              {{ group.title }}
              <span class="text-xs font-normal text-neutral-400"
                >最新 {{ group.items.length }} 条</span
              >
            </h2>
            <button
              @click="switchTab(group.type)"
              class="text-sm text-neutral-400 hover:text-primary transition-colors"
            >
              查看全部 <i class="fa fa-angle-right ml-0.5"></i>
            </button>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <div
              v-for="item in group.items"
              :key="group.type + item.id"
              class="bg-white rounded-xl border border-neutral-200 p-5 hover:shadow-md hover:border-primary/40 transition-all cursor-pointer flex flex-col"
              @click="openItem(item)"
            >
              <div class="flex items-start justify-between gap-2 mb-2">
                <h3
                  class="font-bold text-neutral-900 line-clamp-2 flex-1 group-hover:text-primary"
                >
                  {{ item.title || "未命名" }}
                </h3>
                <span
                  class="text-[11px] px-2 py-0.5 rounded-full flex-shrink-0"
                  :class="group.badge"
                  >{{ group.label }}</span
                >
              </div>
              <p class="text-xs text-neutral-500 line-clamp-2 mb-3 flex-1">
                {{ item.desc || "暂无简介" }}
              </p>
              <div class="flex items-center justify-between text-xs text-neutral-400">
                <span class="truncate">{{ item.meta || "" }}</span>
                <span class="flex-shrink-0">{{ item.time || "" }}</span>
              </div>
            </div>
          </div>
        </section>

        <div
          v-if="totalCount === 0"
          class="bg-white rounded-xl border border-neutral-200 p-12 text-center"
        >
          <i class="fa fa-lightbulb-o text-3xl text-neutral-300 mb-3"></i>
          <p class="text-neutral-500 text-sm mb-4">
            还没有内容，来发布第一个作品或需求吧
          </p>
          <router-link
            to="/submit"
            class="px-5 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
          >
            去发布
          </router-link>
        </div>
      </template>

      <!-- 单类列表 -->
      <template v-else>
        <div
          v-if="currentItems.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          <div
            v-for="item in currentItems"
            :key="item.id"
            class="bg-white rounded-xl border border-neutral-200 p-5 hover:shadow-md hover:border-primary/40 transition-all cursor-pointer flex flex-col"
            @click="openItem(item)"
          >
            <div class="flex items-start justify-between gap-2 mb-2">
              <h3 class="font-bold text-neutral-900 line-clamp-2 flex-1">
                {{ item.title || "未命名" }}
              </h3>
              <span
                class="text-[11px] px-2 py-0.5 rounded-full flex-shrink-0"
                :class="tabMeta.badge"
                >{{ tabMeta.label }}</span
              >
            </div>
            <p class="text-xs text-neutral-500 line-clamp-2 mb-3 flex-1">
              {{ item.desc || "暂无简介" }}
            </p>
            <div class="flex items-center justify-between text-xs text-neutral-400">
              <span class="truncate">{{ item.meta || "" }}</span>
              <span class="flex-shrink-0">{{ item.time || "" }}</span>
            </div>
          </div>
        </div>

        <div
          v-else
          class="bg-white rounded-xl border border-neutral-200 p-12 text-center"
        >
          <i class="fa fa-inbox text-3xl text-neutral-300 mb-3"></i>
          <p class="text-neutral-500 text-sm mb-5">该板块还没有内容</p>
          <router-link
            :to="tabMeta.publish"
            class="px-5 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
          >
            {{ tabMeta.publishLabel }}
          </router-link>
        </div>
      </template>
    </main>
  </div>
</template>

<script>
import { requireLogin } from "../utils/authGuard";

export default {
  name: "CreateSquarePage",
  data() {
    return {
      tab: "all",
      loading: false,
      tools: [],
      demands: [],
      cases: [],
      prompts: [],
      webs: [],
      publishEntries: [
        {
          to: "/submit",
          label: "发布工具",
          desc: "把你的 AI 应用上架到应用广场",
          icon: "fa-plus-circle",
          bg: "bg-primary",
        },
        {
          to: "/request",
          label: "发布需求",
          desc: "说说你希望有人做出什么工具",
          icon: "fa-question-circle-o",
          bg: "bg-amber-500",
        },
        {
          to: "/cases/submit",
          label: "投稿案例",
          desc: "分享 AI 在课堂里的真实用法",
          icon: "fa-file-text-o",
          bg: "bg-emerald-500",
        },
        {
          to: "/prompts",
          label: "分享提示词",
          desc: "沉淀好用的提示词与技巧",
          icon: "fa-bookmark-o",
          bg: "bg-purple-500",
        },
      ],
      tabs: [
        { id: "all", label: "全部" },
        { id: "tool", label: "AI工具" },
        { id: "demand", label: "需求" },
        { id: "case", label: "案例" },
        { id: "web", label: "网站" },
        { id: "prompt", label: "提示词" },
      ],
    };
  },
  computed: {
    groups() {
      return [
        {
          type: "tool",
          title: "最新 AI 工具",
          icon: "fa-cube",
          color: "#2563eb",
          label: "工具",
          badge: "bg-blue-50 text-blue-600",
          items: this.tools,
        },
        {
          type: "demand",
          title: "最新需求",
          icon: "fa-lightbulb-o",
          color: "#d97706",
          label: "需求",
          badge: "bg-amber-50 text-amber-600",
          items: this.demands,
        },
        {
          type: "case",
          title: "最新案例",
          icon: "fa-graduation-cap",
          color: "#059669",
          label: "案例",
          badge: "bg-emerald-50 text-emerald-600",
          items: this.cases,
        },
        {
          type: "web",
          title: "最新网站",
          icon: "fa-window-maximize",
          color: "#0891b2",
          label: "网站",
          badge: "bg-cyan-50 text-cyan-600",
          items: this.webs,
        },
        {
          type: "prompt",
          title: "最新提示词",
          icon: "fa-bookmark",
          color: "#7c3aed",
          label: "提示词",
          badge: "bg-purple-50 text-purple-600",
          items: this.prompts,
        },
      ];
    },
    tabMeta() {
      const map = {
        tool: {
          label: "工具",
          badge: "bg-blue-50 text-blue-600",
          publish: "/submit",
          publishLabel: "去发布工具",
        },
        demand: {
          label: "需求",
          badge: "bg-amber-50 text-amber-600",
          publish: "/request",
          publishLabel: "去提需求",
        },
        case: {
          label: "案例",
          badge: "bg-emerald-50 text-emerald-600",
          publish: "/cases/submit",
          publishLabel: "去投稿案例",
        },
        web: {
          label: "网站",
          badge: "bg-cyan-50 text-cyan-600",
          publish: "/webinfo",
          publishLabel: "去网站集合",
        },
        prompt: {
          label: "提示词",
          badge: "bg-purple-50 text-purple-600",
          publish: "/prompts",
          publishLabel: "去分享提示词",
        },
      };
      return map[this.tab] || map.tool;
    },
    currentItems() {
      const map = {
        tool: this.tools,
        demand: this.demands,
        case: this.cases,
        web: this.webs,
        prompt: this.prompts,
      };
      return map[this.tab] || [];
    },
    totalCount() {
      return (
        this.tools.length +
        this.demands.length +
        this.cases.length +
        this.prompts.length +
        this.webs.length
      );
    },
  },
  created() {
    this.loadAll();
  },
  methods: {
    goPublish(entry) {
      if (!requireLogin(this, "发布内容")) return;
      this.$router.push(entry.to);
    },
    switchTab(id) {
      this.tab = id;
    },
    openItem(item) {
      this.$router.push(item.to);
    },
    /** 时间只展示到日 */
    shortTime(t) {
      if (!t) return "";
      return String(t).slice(0, 10);
    },
    async loadAll() {
      this.loading = true;
      const [tools, demands, cases, prompts, webs] = await Promise.all([
        this.fetchTools(),
        this.fetchDemands(),
        this.fetchCases(),
        this.fetchPrompts(),
        this.fetchWebs(),
      ]);
      this.tools = tools;
      this.demands = demands;
      this.cases = cases;
      this.prompts = prompts;
      this.webs = webs;
      this.loading = false;
    },
    async fetchTools() {
      try {
        const data = await this.$aesEncrypt({
          page: 1,
          pageSize: 6,
          searchKey: "",
          categoryId: "",
          subId: "",
          stageId: "",
          gradeId: "",
          termId: "",
          unitId: "",
        });
        const res = await this.$axios({
          url: "aitool/search",
          method: "POST",
          data: { data },
        });
        if (res.code !== 200) return [];
        return (res.data.list || []).map((t) => ({
          id: t.id,
          title: t.title,
          desc: t.description,
          meta: (t.downloadCount || 0) + " 次下载",
          time: this.shortTime(t.createTime),
          to: "/tool/" + t.id,
        }));
      } catch (e) {
        return [];
      }
    },
    async fetchDemands() {
      // 需求列表需要登录，未登录时不请求，避免 401 被全局拦截器踢到登录页
      if (!localStorage.getItem("token")) return [];
      try {
        const data = await this.$aesEncrypt({ page: 1, pageSize: 6 });
        const res = await this.$axios({
          url: "demand/list",
          method: "POST",
          data: { data },
        });
        if (res.code !== 200) return [];
        return (res.data.list || []).map((d) => ({
          id: d.id,
          title: d.title,
          desc: d.description || d.content || "",
          meta: d.categoryName || d.userName || "站内需求",
          time: this.shortTime(d.createTime),
          to: "/requests",
        }));
      } catch (e) {
        return [];
      }
    },
    async fetchCases() {
      try {
        const data = await this.$aesEncrypt({
          page: 1,
          pageSize: 6,
          scenario: "",
          searchKey: "",
        });
        const res = await this.$axios({
          url: "case/list",
          method: "POST",
          data: { data },
        });
        if (res.code !== 200) return [];
        return (res.data.list || []).map((c) => ({
          id: c.id,
          title: c.title,
          desc: c.scenario || "",
          meta: c.author || "匿名",
          time: this.shortTime(c.createTime),
          to: "/cases",
        }));
      } catch (e) {
        return [];
      }
    },
    async fetchPrompts() {
      try {
        const data = await this.$aesEncrypt({
          page: 1,
          pageSize: 6,
          searchKey: "",
          type: "",
          category: "",
          sort: "",
        });
        const res = await this.$axios({
          url: "prompt/list",
          method: "POST",
          data: { data },
        });
        if (res.code !== 200) return [];
        return (res.data.list || []).map((p) => ({
          id: p.id,
          title: p.title,
          desc: p.content || p.description || "",
          meta: p.type || "提示词",
          time: this.shortTime(p.createTime),
          to: "/prompts",
        }));
      } catch (e) {
        return [];
      }
    },
    async fetchWebs() {
      try {
        const res = await this.$axios({ url: "webinfo/all", method: "POST" });
        if (res.code !== 200 || !Array.isArray(res.data)) return [];
        return res.data.slice(0, 6).map((w) => ({
          id: w.id,
          title: w.title,
          desc: w.description || "",
          meta: w.featured ? "精选站点" : "教育站点",
          time: this.shortTime(w.createTime),
          to: "/webinfo",
        }));
      } catch (e) {
        return [];
      }
    },
  },
};
</script>
